import { useState } from 'react';
import { PaperClipIcon, ChevronRightIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { WithTooltip } from '../../../components/WithTooltip';
import { Mixpanel } from '../../../services/Mixpanel';
import { useDropdownPosition } from '../hooks/useDropdownPosition';
import { useUnlimitedContext } from '../contexts/UnlimitedContext';
import { useUnlimitedModals } from '../contexts/UnlimitedModalsContext';
import { isHoveringPointer } from '../utils/isHoveringPointer';

// Mirrors the MTSS tier options in AdaptMenu: each entry has a short id (sent to the
// backend), a display name, and a one-line description shown under the name in the
// submenu. Kept here (not in a constants file) because they only appear in this menu.
// Display names map Auto/Always/Never; ids remain 'auto'/'on'/'off' for backend + analytics compat.
const WEB_SOURCES_OPTIONS = [
  {
    id: 'auto',
    name: 'Auto',
    description: 'Search the web and cite sources as needed',
  },
  {
    id: 'on',
    name: 'Always',
    description: 'Search the web and cite sources for every request',
  },
  {
    id: 'off',
    name: 'Never',
    description: 'Skip web search; generate from stored knowledge',
  },
];

// Short forms for the row's status pill — submenu uses the longer name (Always/Never)
// for clarity, but the pill stays compact.
const WEB_SOURCES_PILL_LABEL_BY_ID = { auto: 'Auto', on: 'On', off: 'Off' };

/**
 * Dropdown menu button for attaching files, images, links, or books to the Unlimited prompt input.
 * Also exposes the Web sources mode toggle (auto / on / off) as a sub-menu row.
 * Reads state from UnlimitedContext and UnlimitedModalsContext.
 *
 * @param {Object} props
 * @param {boolean} [props.showBooks=true] - false where a book can't be attached (Courses).
 * @param {boolean} [props.showWebSources=true] - false where there's no Web Sources mode (Courses).
 * @param {('top'|'bottom')} [props.tooltipPosition='bottom'] - 'top' under an input at the screen's bottom.
 */
export const AttachMenu = ({
  showBooks = true,
  showWebSources = true,
  tooltipPosition = 'bottom',
}) => {
  const { toolbar, book, attachments, webSources, requireAuthForMenus, trackingSource } =
    useUnlimitedContext();
  // Another surface (Courses) tags its events; the create screen's stay exactly as they were.
  const track = (name, props) =>
    Mixpanel.track(name, trackingSource ? { ...props, source: trackingSource } : props);
  const { showPlusMenu, setShowPlusMenu } = toolbar;
  const { showTopLevelBooks, selectedBook } = book;
  const { pdfFile, imageFiles, MAX_IMAGES, fileInputRef, imageInputRef, setShowLinkModal } =
    attachments;
  const { mode: webSourcesMode, setMode: setWebSourcesMode } = webSources;
  const { setShowBookModal } = useUnlimitedModals();
  const { menuRef, dropUp, leftOffset, maxHeight, recalculate } = useDropdownPosition({
    dropdownWidth: 240,
    minSpaceBelow: 360,
  });

  const [showWebSourcesSubmenu, setShowWebSourcesSubmenu] = useState(false);
  const [submenuMobileStyle, setSubmenuMobileStyle] = useState(null);

  const isWebSourcesActive = webSourcesMode === 'on' || webSourcesMode === 'off';
  const webSourcesPillLabel = WEB_SOURCES_PILL_LABEL_BY_ID[webSourcesMode] || 'Auto';

  // Compute submenu's mobile width / right-edge anchor based on the parent menu's
  // bounding box. Mirrors AdaptMenu's adaptSubmenuMobileStyle pattern.
  const computeSubmenuMobileStyle = () => {
    const rect = menuRef.current?.getBoundingClientRect();
    if (!rect || window.innerWidth >= 848) {
      setSubmenuMobileStyle(null);
      return;
    }
    const dropdownWidth = 240;
    const overflow = rect.left + dropdownWidth - window.innerWidth + 16;
    const freshLeftOffset = overflow > 0 ? -overflow : 0;
    const parentRight = rect.left + freshLeftOffset + dropdownWidth;
    const spaceRight = Math.max(0, window.innerWidth - 16 - parentRight);
    setSubmenuMobileStyle({ right: -spaceRight, width: 224 + spaceRight });
  };

  return (
    <div className='relative ml-3' ref={menuRef}>
      <WithTooltip
        text={
          showPlusMenu
            ? ''
            : showTopLevelBooks || !showBooks
              ? 'Upload a PDF or image'
              : 'Upload a PDF or image, or select a book'
        }
        position={tooltipPosition}
        anchor='center'
        size='xs'
        showArrow>
        <button
          onClick={() => {
            requireAuthForMenus(() => {
              if (!showPlusMenu) {
                track('Unlimited Attach Clicked');
                recalculate();
                computeSubmenuMobileStyle();
              } else {
                setShowWebSourcesSubmenu(false);
              }
              setShowPlusMenu(!showPlusMenu);
            });
          }}
          className='flex items-center gap-1.5 py-2 px-3 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors border border-transparent'
          aria-label='Attach file or image'>
          <PaperClipIcon className='w-5 h-5' />
          <span className='text-sm hidden md:inline'>Attach</span>
        </button>
      </WithTooltip>

      {/* Attach Menu Dropdown. Capped to the viewport via maxHeight (from the hook). All rows
          (incl. Web Sources) scroll inside an inner wrapper; the Web Sources submenu is a sibling
          of that wrapper so the scroll container can't clip it (overflow-y-auto forces overflow-x
          to clip a left-full submenu). */}
      {showPlusMenu && (
        <>
          <div
            className='fixed inset-0 z-40'
            onClick={() => {
              setShowPlusMenu(false);
              setShowWebSourcesSubmenu(false);
            }}
          />
          <div
            className={`absolute flex flex-col w-60 bg-white rounded-xl shadow-lg border border-gray-200 p-1.5 z-50 ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
            style={{ left: leftOffset, maxHeight }}
            onClick={() => setShowWebSourcesSubmenu(false)}>
            <div className='min-h-0 overflow-y-auto overscroll-contain scrollbar-dropdown'>
              <button
                onClick={() => {
                  track('Unlimited Upload PDF Clicked');
                  setShowPlusMenu(false);
                  fileInputRef.current?.click();
                }}
                onMouseEnter={() => setShowWebSourcesSubmenu(false)}
                disabled={!!pdfFile}
                className='w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed'>
                <div>
                  <span>Upload PDF</span>
                </div>
                <div className='text-xs text-gray-500'>Up to 50MB</div>
              </button>
              <button
                onClick={() => {
                  track('Unlimited Upload Image Clicked');
                  setShowPlusMenu(false);
                  imageInputRef.current?.click();
                }}
                onMouseEnter={() => setShowWebSourcesSubmenu(false)}
                disabled={imageFiles.length >= MAX_IMAGES}
                className='w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed'>
                <div>
                  <span>Upload Image</span>
                </div>
                <div className='text-xs text-gray-500'>PNG, JPG, GIF, WebP (up to 5MB)</div>
              </button>
              <button
                onClick={() => {
                  track('Unlimited Paste Link Clicked');
                  setShowPlusMenu(false);
                  setShowLinkModal(true);
                }}
                onMouseEnter={() => setShowWebSourcesSubmenu(false)}
                className='w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm'>
                <div>
                  <span>Paste a Link</span>
                </div>
                <div className='text-xs text-gray-500'>Webpage or PDF</div>
              </button>
              {showBooks && (
                <button
                  onClick={() => {
                    track('Unlimited From Library Clicked');
                    setShowPlusMenu(false);
                    setShowBookModal(true);
                  }}
                  onMouseEnter={() => setShowWebSourcesSubmenu(false)}
                  disabled={!!selectedBook}
                  className={`w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed ${showTopLevelBooks ? 'md:hidden' : ''}`}>
                  <div>
                    <span>Choose a Book</span>
                  </div>
                  <div className='text-xs text-gray-500'>1,000+ titles available</div>
                </button>
              )}
              {/* Web Sources row scrolls with the file items above; its submenu is rendered as a
                  sibling of this scroll wrapper (below) so the scroll container can't clip it. */}
              {showWebSources && (
                <div
                  onPointerEnter={(e) => {
                    if (isHoveringPointer(e)) setShowWebSourcesSubmenu(true);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowWebSourcesSubmenu((prev) => !prev);
                  }}
                  className='w-full flex items-start justify-between px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm cursor-default'>
                  <span className='flex flex-col'>
                    <span>Web Sources</span>
                    <span className='text-xs text-gray-500'>Find and cite sources</span>
                  </span>
                  <span className='flex items-center gap-1.5 min-w-0'>
                    <span
                      className={`${isWebSourcesActive ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-600'} inline-flex items-center min-w-0 px-2 py-0.5 rounded-full text-xs max-w-[7rem]`}>
                      <span className='truncate'>{webSourcesPillLabel}</span>
                      {isWebSourcesActive && (
                        <button
                          type='button'
                          onClick={(e) => {
                            e.stopPropagation();
                            setWebSourcesMode('auto');
                            track('Unlimited Web Sources Mode Set', { mode: 'auto' });
                          }}
                          className='-my-0.5 -mr-2 ml-0.5 flex items-center justify-center self-stretch w-5 shrink-0 rounded-full hover:bg-emerald-200 transition-colors'
                          aria-label='Reset web sources to auto'>
                          <XMarkIcon className='w-2.5 h-2.5' />
                        </button>
                      )}
                    </span>
                    <ChevronRightIcon className='w-3.5 h-3.5 text-gray-400 shrink-0' />
                  </span>
                </div>
              )}
            </div>

            {/* Web Sources submenu — sibling of the scroll wrapper, positioned relative to the
                menu so it escapes the scroll container's clip. */}
            {showWebSourcesSubmenu && (
              <div
                className={`absolute bottom-0 ${submenuMobileStyle ? '' : 'right-0 w-64'} md:right-auto md:left-full md:-ml-2 md:w-64 max-h-64 overflow-y-auto overscroll-contain scrollbar-dropdown bg-white rounded-xl shadow-lg border border-gray-200 p-1.5 z-50`}
                style={submenuMobileStyle || undefined}
                onClick={(e) => e.stopPropagation()}>
                {WEB_SOURCES_OPTIONS.map((opt) => {
                  const isSelected = webSourcesMode === opt.id;
                  const isAutoDefault = opt.id === 'auto';
                  return (
                    <button
                      key={opt.id}
                      onClick={() => {
                        // Mutually exclusive — clicking the active non-default option
                        // resets back to 'auto' (matches MTSS Tier 2/3 behavior).
                        const next = !isAutoDefault && isSelected ? 'auto' : opt.id;
                        setWebSourcesMode(next);
                        track('Unlimited Web Sources Mode Set', { mode: next });
                      }}
                      className={`w-full flex items-start justify-between gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm text-left ${
                        isSelected
                          ? isAutoDefault
                            ? 'bg-gray-50 text-gray-900'
                            : 'bg-emerald-50 text-emerald-800'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}>
                      <span className='flex flex-col'>
                        <span>{opt.name}</span>
                        <span className='text-xs text-gray-500'>{opt.description}</span>
                      </span>
                      {isSelected ? (
                        <CheckIcon
                          className={`w-4 h-4 mt-0.5 shrink-0 ${isAutoDefault ? 'text-gray-900' : ''}`}
                        />
                      ) : isAutoDefault ? (
                        <span className='text-gray-400 text-xs mt-0.5 shrink-0'>default</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
